import { Button, Result, Spin } from 'antd'
import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'

/** `checking` | `unavailable` | `known` (me is a user or definitively null). */
export function useEnsureAuthChecked(): 'checking' | 'unavailable' | 'known' {
  const { me, loading, authUnavailable, refresh } = useAuth()
  useEffect(() => {
    if (me === undefined && !loading && !authUnavailable) {
      void refresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, loading, authUnavailable])
  if (me !== undefined) return 'known'
  if (authUnavailable && !loading) return 'unavailable'
  return 'checking'
}

function FullPageSpin() {
  return (
    <div style={{ height: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin size="large" />
    </div>
  )
}

/** Session could not be checked (timeout / network / 5xx) — NOT logged out. */
export function AuthUnavailable() {
  const { refresh } = useAuth()
  return (
    <Result
      status="warning"
      title="暂时无法连接登录服务，请稍后重试"
      subTitle="您的登录状态仍然保留，服务恢复后即可继续使用。"
      extra={
        <Button type="primary" onClick={() => void refresh()} data-testid="auth-unavailable-retry">
          重试
        </Button>
      }
    />
  )
}

/** Any logged-in Vistoera user (Platform Admin or Tenant Admin alike) —
 * backend still enforces per-route/per-workspace authorization on every
 * request; this guard only avoids flashing protected UI before we know
 * the session is invalid (spec §15/§31 — never itself a security
 * boundary). */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  const authState = useEnsureAuthChecked()
  const location = useLocation()

  if (authState === 'checking') return <FullPageSpin />
  if (authState === 'unavailable') return <AuthUnavailable />
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <>{children}</>
}

/** `/platform/*` — spec §31: requires `platform_admin`, distinct scope
 * from Workspace membership. */
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  const authState = useEnsureAuthChecked()
  const location = useLocation()

  if (authState === 'checking') return <FullPageSpin />
  if (authState === 'unavailable') return <AuthUnavailable />
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  if (!me.is_platform_admin) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="This account does not have Platform Admin access."
      />
    )
  }
  return <>{children}</>
}
